'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import type { TrustMetric } from '@/constants/homeMarketing';
import { P, Span, Strong, SURFACE_REVEAL } from '@/components/ui';

export type OutcomesMetricsSectionProps = {
  title: string;
  subtitle?: string;
  metrics: readonly TrustMetric[];
  sectionId?: string;
  className?: string;
};

function metricsGridClass(count: number): string {
  if (count <= 4) return 'page-grid-4';
  return 'page-grid-metrics';
}

/** Compact proof row for consulting / services landing pages (glass stat tiles). */
const OutcomesMetricsSection: React.FC<OutcomesMetricsSectionProps> = ({
  title,
  subtitle,
  metrics,
  sectionId,
  className,
}) => (
  <section
    id={sectionId}
    className={clsx('container-section page-section-band page-section-band--muted', className)}
    aria-labelledby="outcomes-metrics-heading"
  >
    <div className="page-heading-wrap">
      <div id="outcomes-metrics-heading">
        <Heading2
          title={title}
          subtitle={subtitle}
          titleClassName="text-prose"
          subtitleClassName="mt-3 text-prose-muted"
        />
      </div>
    </div>
    <div className={clsx('page-content-grid', metricsGridClass(metrics.length))}>
      {metrics.map((m) => (
        <div key={m.id} className={clsx('page-stat-tile', SURFACE_REVEAL)}>
          <Strong className="text-xl font-semibold tracking-tight text-prose tabular-nums xs:text-2xl sm:text-3xl">
            {m.value}
          </Strong>
          <Span className="text-xs font-medium text-prose xs:text-sm">{m.label}</Span>
          {m.hint ? (
            <P className="m-0 text-[0.6875rem] leading-snug text-prose-muted xs:text-xs">{m.hint}</P>
          ) : null}
        </div>
      ))}
    </div>
  </section>
);

export default OutcomesMetricsSection;
